import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  CubeIcon,
  ArrowRightIcon,
  ClockIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  UserIcon
} from '@heroicons/react/24/outline';
import { bridgeAPI } from '../services/api';
import QuantumSecurityIndicator from './QuantumSecurityIndicator';
import ComplianceDashboard from './ComplianceDashboard';
import EcosystemNav from './EcosystemNav';
import SimulationStatusBanner from './SimulationStatusBanner';

const DEFAULT_METAVERSES = [
  { id: 1, name: 'Galaxia Native', description: 'Native Galaxia metaverse identity and assets', isActive: true },
  { id: 2, name: 'Ready Player Me', description: 'Universal avatar system for the metaverse', isActive: true },
  { id: 3, name: 'OMA3', description: 'Open Metaverse Alliance standards', isActive: true }
];

const DEMO_ACTIVITY = [
  {
    id: 1,
    type: 'bridge',
    description: 'Sample: Asset bridged to Ready Player Me (simulation)',
    timestamp: new Date(Date.now() - 1000 * 60 * 30),
    status: 'success'
  },
  {
    id: 2,
    type: 'avatar',
    description: 'Sample: Avatar portability package generated (simulation)',
    timestamp: new Date(Date.now() - 1000 * 60 * 60 * 2),
    status: 'success'
  }
];

const Dashboard = ({ userAddress, bridgeStatus, onToggleSidebar }) => {
  const [stats, setStats] = useState({
    lockedAssets: 0,
    wrappedAssets: 0,
    bridgedAssets: 0,
    activeAvatars: 0
  });
  const [recentActivity, setRecentActivity] = useState([]);
  const [supportedMetaverses, setSupportedMetaverses] = useState(DEFAULT_METAVERSES);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);

  const loadDashboardData = useCallback(async () => {
    setLoading(true);
    setLoadError(null);

    try {
      const metaversesResponse = await bridgeAPI.getSupportedMetaverses();
      const metaverseList = metaversesResponse?.data ?? metaversesResponse;
      if (Array.isArray(metaverseList) && metaverseList.length > 0) {
        setSupportedMetaverses(metaverseList);
      }

      if (userAddress) {
        const { avatarAPI } = await import('../services/api');
        const [assetsResponse, avatarResponse] = await Promise.all([
          bridgeAPI.getUserAssets(userAddress),
          avatarAPI.getUserAvatars(userAddress)
        ]);

        const assets = assetsResponse?.data ?? assetsResponse ?? {};
        const avatars = avatarResponse?.data ?? avatarResponse ?? {};

        setStats({
          lockedAssets: assets.lockedAssets?.length || 0,
          wrappedAssets: assets.wrappedAssets?.length || 0,
          bridgedAssets: (assets.lockedAssets?.length || 0) + (assets.wrappedAssets?.length || 0),
          activeAvatars: avatars.avatars?.length || 0
        });
        setRecentActivity(DEMO_ACTIVITY);
      } else {
        setStats({ lockedAssets: 0, wrappedAssets: 0, bridgedAssets: 0, activeAvatars: 0 });
        setRecentActivity(DEMO_ACTIVITY);
      }
    } catch (error) {
      console.error('Failed to load dashboard data:', error);
      setLoadError('Some dashboard data is unavailable. Showing simulation defaults.');
      setSupportedMetaverses(DEFAULT_METAVERSES);
      setRecentActivity(DEMO_ACTIVITY);
    } finally {
      setLoading(false);
    }
  }, [userAddress]);

  useEffect(() => {
    loadDashboardData();
  }, [loadDashboardData]);

  const formatTimeAgo = (timestamp) => {
    const now = new Date();
    const diff = now - timestamp;
    if (diff < 1000 * 60) return 'Just now';
    if (diff < 1000 * 60 * 60) return `${Math.floor(diff / (1000 * 60))} minutes ago`;
    if (diff < 1000 * 60 * 60 * 24) return `${Math.floor(diff / (1000 * 60 * 60))} hours ago`;
    return `${Math.floor(diff / (1000 * 60 * 60 * 24))} days ago`;
  };

  const getStatusIcon = (status) => {
    const style = (c) => ({ color: c });
    switch (status) {
      case 'success':
        return <CheckCircleIcon className="h-5 w-5" style={style('var(--galaxia-success)')} />;
      case 'pending':
        return <ClockIcon className="h-5 w-5" style={style('var(--galaxia-warning)')} />;
      case 'error':
        return <ExclamationTriangleIcon className="h-5 w-5" style={style('var(--galaxia-error)')} />;
      default:
        return <ClockIcon className="h-5 w-5" style={style('var(--galaxia-muted)')} />;
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="galaxia-card p-8 animate-pulse">
          <div className="h-8 w-64 rounded mb-4" style={{ background: 'var(--galaxia-surface)' }} />
          <div className="h-4 w-full max-w-xl rounded mb-2" style={{ background: 'var(--galaxia-surface)' }} />
          <div className="h-4 w-2/3 max-w-md rounded" style={{ background: 'var(--galaxia-surface)' }} />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="galaxia-card p-6 h-24 animate-pulse" style={{ background: 'rgba(255,255,255,0.03)' }} />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 galaxia-fade-up">
      <motion.div
        className="galaxia-card p-8 relative overflow-hidden"
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.25 }}
      >
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
          <div>
            <img
              src={`${process.env.PUBLIC_URL || ''}/brand-lockup.svg`}
              alt="Galaxia Metaverse Bridge"
              className="galaxia-logo-glow h-14 mb-4"
            />
            <h1 className="text-3xl md:text-4xl font-bold font-heading" style={{ color: 'var(--galaxia-text)' }}>
              Move assets and avatars across worlds
            </h1>
            <p className="mt-3 text-lg max-w-2xl" style={{ color: 'var(--galaxia-text)' }}>
              Move assets, avatars, and identity across connected worlds.
            </p>
            <p className="mt-2 max-w-xl text-sm" style={{ color: 'var(--galaxia-muted)' }}>
              {userAddress
                ? `Wallet connected: ${userAddress.slice(0, 6)}…${userAddress.slice(-4)}`
                : 'Galaxia Metaverse Bridge moves assets and avatars across connected worlds.'}
            </p>
            <div className="mt-6 flex flex-col sm:flex-row gap-3">
              <Link to="/bridge" className="galaxia-btn-primary px-5 py-3 text-sm font-medium inline-flex items-center justify-center">
                <ArrowRightIcon className="h-5 w-5 mr-2" />
                Bridge Asset
              </Link>
              <Link to="/assets" className="galaxia-btn-secondary px-5 py-3 text-sm font-medium inline-flex items-center justify-center">
                <CubeIcon className="h-5 w-5 mr-2" />
                Explore Assets
              </Link>
              <Link to="/avatar" className="galaxia-btn-secondary px-5 py-3 text-sm font-medium inline-flex items-center justify-center">
                <UserIcon className="h-5 w-5 mr-2" />
                Manage Avatar
              </Link>
            </div>
          </div>
          <div className="flex flex-col items-start md:items-end gap-3">
            <QuantumSecurityIndicator />
            <button
              onClick={onToggleSidebar}
              className="lg:hidden p-2 rounded-lg"
              style={{ color: 'var(--galaxia-muted)' }}
              type="button"
              aria-label="Open menu"
            >
              <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </button>
          </div>
        </div>
      </motion.div>

      <SimulationStatusBanner />

      {loadError && (
        <div className="galaxia-card px-4 py-3 text-sm" style={{ color: 'var(--galaxia-warning)' }} role="status">
          {loadError}
        </div>
      )}

      <div className="galaxia-card p-6">
        <h2 className="text-lg font-semibold mb-4" style={{ color: 'var(--galaxia-text)' }}>Connected Ecosystems</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {DEFAULT_METAVERSES.map((mv) => (
            <div
              key={mv.id}
              className="rounded-xl p-4"
              style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid var(--galaxia-border)' }}
            >
              <p className="font-medium" style={{ color: 'var(--galaxia-text)' }}>{mv.name}</p>
              <p className="text-xs mt-1" style={{ color: 'var(--galaxia-muted)' }}>{mv.description}</p>
            </div>
          ))}
        </div>
      </div>

      <EcosystemNav />

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {[
          { icon: CubeIcon, label: 'Locked Assets', value: stats.lockedAssets, accent: 'var(--galaxia-accent)' },
          { icon: ArrowRightIcon, label: 'Wrapped Assets', value: stats.wrappedAssets, accent: 'var(--galaxia-success)' },
          { icon: UserIcon, label: 'Active Avatars', value: stats.activeAvatars, accent: 'var(--galaxia-accent)' },
          { icon: CheckCircleIcon, label: 'Total Bridged', value: stats.bridgedAssets, accent: 'var(--galaxia-secondary-cta)' }
        ].map(({ icon: Icon, label, value, accent }) => (
          <div key={label} className="galaxia-card p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0" style={{ color: accent }}>
                <Icon className="h-8 w-8" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium" style={{ color: 'var(--galaxia-muted)' }}>{label}</p>
                <p className="text-2xl font-semibold" style={{ color: 'var(--galaxia-text)' }}>{value}</p>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="galaxia-card p-6">
          <h3 className="text-lg font-semibold mb-4" style={{ color: 'var(--galaxia-text)' }}>Bridge Status</h3>
          {bridgeStatus?.bridge ? (
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm" style={{ color: 'var(--galaxia-muted)' }}>Status</span>
                <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                  bridgeStatus.bridge.isActive ? 'galaxia-status-success' : 'galaxia-status-error'
                }`}>
                  {bridgeStatus.bridge.isActive ? 'Active' : 'Inactive'}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm" style={{ color: 'var(--galaxia-muted)' }}>Supported Metaverses</span>
                <span className="text-sm font-medium" style={{ color: 'var(--galaxia-text)' }}>
                  {bridgeStatus.bridge.supportedMetaverses}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm" style={{ color: 'var(--galaxia-muted)' }}>Total Assets Locked</span>
                <span className="text-sm font-medium" style={{ color: 'var(--galaxia-text)' }}>
                  {bridgeStatus.bridge.totalAssetsLocked}
                </span>
              </div>
            </div>
          ) : (
            <p style={{ color: 'var(--galaxia-muted)' }}>Bridge status loading or unavailable — simulation mode active.</p>
          )}
        </div>

        <div className="galaxia-card p-6">
          <h3 className="text-lg font-semibold mb-4" style={{ color: 'var(--galaxia-text)' }}>Supported Metaverses</h3>
          <div className="space-y-3">
            {(Array.isArray(supportedMetaverses) ? supportedMetaverses : DEFAULT_METAVERSES).slice(0, 3).map((metaverse) => (
              <div key={metaverse.id} className="flex items-center justify-between">
                <div className="flex items-center">
                  <div className="w-3 h-3 rounded-full flex-shrink-0" style={{ background: 'var(--galaxia-success)' }} />
                  <div className="ml-3">
                    <p className="text-sm font-medium" style={{ color: 'var(--galaxia-text)' }}>{metaverse.name}</p>
                    <p className="text-xs" style={{ color: 'var(--galaxia-muted)' }}>{metaverse.description}</p>
                  </div>
                </div>
                <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                  metaverse.isActive ? 'galaxia-status-success' : 'galaxia-status-muted'
                }`}>
                  {metaverse.isActive ? 'Active' : 'Inactive'}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="galaxia-card p-6">
        <h3 className="text-lg font-semibold mb-4" style={{ color: 'var(--galaxia-text)' }}>Recent Activity</h3>
        <p className="text-xs mb-3" style={{ color: 'var(--galaxia-muted)' }}>Sample activity in simulation mode</p>
        <div className="space-y-3">
          {recentActivity.map((activity) => (
            <div key={activity.id} className="flex items-center justify-between py-2">
              <div className="flex items-center">
                <div className="flex-shrink-0">{getStatusIcon(activity.status)}</div>
                <div className="ml-3">
                  <p className="text-sm font-medium" style={{ color: 'var(--galaxia-text)' }}>{activity.description}</p>
                  <p className="text-xs" style={{ color: 'var(--galaxia-muted)' }}>{formatTimeAgo(activity.timestamp)}</p>
                </div>
              </div>
              <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                activity.status === 'success' ? 'galaxia-status-success' : 'galaxia-status-warning'
              }`}>
                {activity.status}
              </span>
            </div>
          ))}
        </div>
      </div>

      <ComplianceDashboard />

      <div className="galaxia-card p-6">
        <h3 className="text-lg font-semibold mb-4" style={{ color: 'var(--galaxia-text)' }}>Quick Actions</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Link to="/bridge" className="galaxia-btn-primary flex items-center justify-center px-4 py-3 text-sm font-medium">
            <ArrowRightIcon className="h-5 w-5 mr-2" />
            Bridge Asset
          </Link>
          <Link to="/avatar" className="galaxia-btn-primary flex items-center justify-center px-4 py-3 text-sm font-medium">
            <UserIcon className="h-5 w-5 mr-2" />
            Manage Avatar
          </Link>
          <Link to="/assets" className="galaxia-btn-secondary flex items-center justify-center px-4 py-3 text-sm font-medium">
            <CubeIcon className="h-5 w-5 mr-2" />
            View Assets
          </Link>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
